<template>
  <PageContent>
    <ProductDetailSkeleton
      v-if="fetching && false"
      :with-variant-box="true"
      :with-text-info-box="true"
      :with-attributes="true"
    />
    <div v-else-if="product && (parseInt(packageUnit, 10) || 0) > -1">
      <ProductDetailTwoColumns
        :product="product"
        :selected-variant="selectedVariant"
        :get-attribute="getProductAttribute(product, selectedVariant)"
        :has-siblings="hasSiblings"
        :product-detail-properties="PRODUCT_DETAIL_ATTRIBUTE_KEYS_EYEWEAR"
        :detail-name-mapping="detailNameMapping"
        :back-url="backUrl"
        :product-name="productName"
        :product-color="productColor"
        @on-vto-action="onVtoAction"
        @on-vto-open="() => (interatedWithVTO = true)"
      >
        <template #actions>
          <div class="flex flex-col gap-6">
            <ProductPromotionGiftConditionBanner
              v-if="!areGiftConditionsMet && isGiftAddedToBasket"
              :product="product"
            />
            <ProductPromotionBanners v-else :product="product" />
            <div class="price-summary border-b border-gray-350 pb-6">
              <div class="flex items-baseline justify-between">
                <ProductPrice
                  size="2xl"
                  :price="price"
                  no-margin
                  :applied-reductions="price.appliedReductions"
                  :show-price-from="rxConfiguratorEnabled || hasSpecial"
                  :show-price-reduction-badge="hasSpecial"
                  :zero-cost-product="isZeroCostProduct"
                  :zero-cost-info-slug="zeroCostInfoSlug"
                  :show-discount="true"
                  data-testid="product-price"
                />
                <div
                  v-if="needsPriceOverlay"
                  class="flex gap-2"
                  data-testid="show-price-overlay"
                >
                  <Headline
                    class="ml-auto"
                    weight="normal"
                    tag="span"
                    look="h5"
                  >
                    {{
                      rxConfiguratorEnabled
                        ? $t('pdp.glasses.price_online_heading')
                        : $t('pdp.glasses.price_heading')
                    }}
                  </Headline>
                  <FimButton
                    no-padding
                    type="link"
                    :aria-label="
                      $t('store_finder.details_page.content.more_info')
                    "
                    data-testid="pdp_button_info"
                    @click="showPriceOverlay"
                  >
                    <template #icon>
                      <LazyIconFielmannInfoCircle class="size-5" />
                    </template>
                  </FimButton>
                </div>
              </div>
              <ProductDetailDescriptionText>
                <span class="mt-2 inline-block text-xs leading-5">
                  <template v-if="!isZeroCostProduct && price.withTax > 0">
                    {{ $t('pdp.glasses.with_tax') + ' ' }}
                  </template>
                  <template v-if="isZeroCostProduct">
                    {{ $t('zero_cost_frames.price_hint') + ' ' }}
                  </template>
                  <template v-if="rxConfiguratorEnabled">
                    <ProductShippingCosts
                      :price="price"
                      :quantity="quantity"
                      :product-id="product.id"
                      :is-rx="true"
                      text-size="xs"
                    />
                    <span>
                      {{ ` – ${$t('pdp.delivery_location_information')}` }}
                    </span>
                  </template>
                  <span v-if="lowestPrice" class="flex text-gray-600">
                    {{ $t('pdp.lowest_price') }}
                    {{ `${lowestPrice}` }}
                  </span>
                </span>
              </ProductDetailDescriptionText>
            </div>
            <ProductDetailSizePicker
              :package-unit="selectedPackageUnit"
              :package-units="packageUnits"
              @update:package-unit="onSwitchVariant"
            />
            <FaceScan :variant="selectedVariant" />
            <div v-if="hasSiblings" class="w-full">
              <Headline tag="span" look="h5">
                {{ $t('pdp.colors_heading') }}
              </Headline>
              <ProductDetailSiblingSlider
                class="mt-3 max-w-[100vw]"
                :product="product"
                :ignore-sold-out="!rxConfiguratorEnabled"
              />
            </div>
            <div>
              <div class="h-fit rounded-lg bg-gray-150 px-5 py-6 lg:px-8">
                <template v-if="rxConfiguratorEnabled">
                  <div
                    class="flex"
                    :class="isRopoSupported ? 'flex-col-reverse' : 'flex-col'"
                  >
                    <div
                      v-if="
                        !isFromRopoPlp || (!isRopoSupported && isFromRopoPlp)
                      "
                    >
                      <ProductDetailDeliveryEstimates
                        :key="deliveryEstimatesKey"
                        :stock="stock"
                        :sales-status="salesStatus"
                        :icon-size="2.5"
                        :is-rx-glasses="true"
                        :class="[isRopoSupported ? 'mb-2 mt-4' : 'mb-4']"
                        :product="product"
                        class="justify-center !font-normal"
                      />
                      <FimButton
                        v-if="isRxConfiguratorAvailable"
                        data-testid="openRxConfigurator"
                        :disabled="
                          !isProductAvailable(product) ||
                          !isVariantAvailable(product, selectedVariant)
                        "
                        class="w-full"
                        :type="isRopoSupported ? 'secondary' : 'primary'"
                        :class="[isRopoSupported ? 'mb-0' : 'mb-2']"
                        @mousedown="openRxConfigurator"
                      >
                        <template v-if="!!$currentShop?.rx?.homeTryOn">
                          {{
                            $t('pdp.correctional_glasses.with_glasses_from', {
                              combinedPrice,
                            })
                          }}
                        </template>
                        <template v-else>
                          {{
                            $t('pdp.correctional_glasses.now_choose_glasses')
                          }}
                        </template>
                        <template #iconAfter>
                          <LazyIconFielmannArrowRight
                            v-if="!isRopoSupported"
                            class="size-6"
                          />
                        </template>
                      </FimButton>
                    </div>

                    <div
                      v-if="
                        !isFromRopoPlp || (!isRopoSupported && isFromRopoPlp)
                      "
                      class="text-center text-[15px] font-normal text-gray-600"
                    >
                      <span>{{ $t('pdp.correctional_glasses.or') }}</span>
                    </div>

                    <FimButton
                      v-if="isRopoSupported"
                      data-testid="addItemToRopoBasket"
                      :disabled="
                        !isProductAvailable(product) ||
                        !isVariantAvailable(product, selectedVariant)
                      "
                      type="primary"
                      :class="[
                        isFromRopoPlp && isRopoSupported ? 'mb-0' : 'mb-2',
                      ]"
                      @mousedown="addItemToRopoBasket"
                    >
                      <span>{{ $t('ropo.add_to_basket.cta') }}</span>
                      <template #iconAfter>
                        <LazyIconFielmannArrowRight class="size-6" />
                      </template>
                    </FimButton>

                    <ProductPromotionGifts
                      v-if="isBuyXGetYPrioritized && !isGiftAddedToBasket"
                      :product="product"
                      class="mt-6"
                    />
                  </div>

                  <RxConfiguratorOverlay
                    v-if="rxConfiguratorProps"
                    :config="rxConfiguratorProps"
                    @add-frame-to-basket="addFrameToBasket"
                    @add-to-basket="rxConfiguratorAddToBasket"
                    @on-vto-open="interatedWithVTO = true"
                    @close="closeRxConfigurator"
                  />
                </template>

                <FimButton
                  v-if="!isRopoSupported"
                  data-testid="pdp_button_appointment"
                  :type="rxConfiguratorEnabled ? 'secondary' : 'primary'"
                  class="mt-2 w-full"
                  target="_blank"
                  :to="appointmentUrl"
                  @mousedown="onCTAClick"
                >
                  {{ $t('pdp.schedule_appointment_button') }}
                </FimButton>

                <template v-if="!rxConfiguratorEnabled">
                  <div class="mb-2 mt-5 text-[17px] font-medium leading-[22px]">
                    {{ $t('pdp.correctional_glasses.card_headline') }}
                  </div>
                  <p class="text-[17px] leading-[22px]">
                    {{ $t('pdp.correctional_glasses.card_text') }}
                  </p>
                </template>
              </div>
              <BenefitsList
                v-if="rxConfiguratorEnabled"
                class="mt-6"
                :slug="benefitsSlug"
              />
            </div>
          </div>
        </template>
        <template #manuals>
          <div v-if="manualUrl || complianceUrl">
            <Headline class="mb-6" tag="span" look="h3">
              {{ $t('pdp.manual.headline') }}
            </Headline>
            <ProductDetailManualLink
              v-if="manualUrl"
              class="mb-6"
              :url="manualUrl"
              translation-key="pdp.manual.copyGlasses"
            />
            <ProductDetailManualLink
              v-if="complianceUrl"
              :url="complianceUrl"
              translation-key="pdp.manual.copyCompliance"
              link-translation-key="pdp.manual.linkCompliance"
            />
          </div>
        </template>
      </ProductDetailTwoColumns>
      <div
        v-if="isSyteEnabled && syteProductImageUrl"
        id="syte-similar-items-container"
        :data-sku="selectedVariantId[0] ?? product.id"
        :data-image-src="syteProductImageUrl"
        data-placement="PDP"
      ></div>
      <FimRecommendations
        v-if="recommendationRefXsell"
        :recommendation-product="product"
        :product-recommendations="recommendationRefXsell"
        :title="getRecommendationTitle('recommendationRefXsell', $i18n)"
        class="mt-12"
        location="PDP"
      >
        <FimRecommendationLinks field-name="rx_glasses_links" />
      </FimRecommendations>
      <BazaarVoice :id="product.id" show="reviews" />
      <ClientOnly>
        <Overlay
          v-if="rxConfiguratorEnabled"
          :title="$t('pdp.glasses.overlay.price_online_title')"
          :open="isOnlinePriceOverlayVisible"
          portal-target="#price-online-overlay"
          @update:open="isOnlinePriceOverlayVisible = $event"
        >
          <Headline tag="h2" class="mb-1">
            {{ $t('pdp.glasses.price_online_heading') }}
          </Headline>
          <div class="mb-6 text-lg leading-6">
            {{ $t('pdp.glasses.overlay.price_online_text') }}
          </div>
          <RxProductPriceBreakdown
            v-if="selectedVariant"
            :product="product"
            :variant="selectedVariant"
        /></Overlay>
        <Overlay
          v-else-if="!rxConfiguratorEnabled"
          :title="$t('pdp.glasses.overlay.price_title')"
          :open="isPriceOverlayVisible"
          portal-target="#pdp-price-overlay"
          @update:open="isPriceOverlayVisible = $event"
        >
          <h2 class="mb-4 text-[24px] font-medium leading-[32px]">
            {{ $t('pdp.glasses.price_heading') }}
          </h2>
          <div class="text-lg leading-6">
            {{ $t('pdp.glasses.overlay.price_text') }}
          </div>
        </Overlay>
      </ClientOnly>

      <div id="pdp-price-overlay" data-testid="price-overlay"></div>
      <div id="price-online-overlay" data-testid="price-online-overlay"></div>
    </div>
  </PageContent>
</template>

<script setup lang="ts">
import { ExistingItemHandling } from '@scayle/storefront-api'
import type { Product, Variant } from '@scayle/storefront-nuxt'
import { useGlasses } from '~/composables/pdp/useGlasses'
import { useFimBasket } from '~/composables/useFimBasket'
import { PRODUCT_DETAIL_ATTRIBUTE_KEYS_EYEWEAR } from '~/constants/product'
import { getLowestPriorPrice } from '~/utils/price'
import {
  getRecommendationTitle,
  isHomeTryOnEligible,
  isProductAvailable,
  isVariantAvailable,
  ProductCategories,
} from '~/utils/product'
import { getQueryParam } from '~/utils/route'
import {
  generateBreadcrumbSchema,
  generateProductSchema,
  getBreadcrumbSchemaItems,
} from '~/utils/seo'

const { setPageState } = usePageState()
const scope = effectScope()
onScopeDispose(() => scope.stop())
const props = defineProps({
  baseProduct: {
    type: Object as PropType<Product>,
    required: true,
  },
})

const context = useNuxtApp()

const { faceWidth } = useFaceWidth()
const { trackPageView } = useTrackingEvents()
const { $fimLocalePath, $i18n, $currentShop } = context
const route = useRoute()
const queryVariantId = computed(() => getQueryParam(route.query, 'variantid'))
const { getProductDetailRoute } = useRouteHelpers()

const {
  isRxConfiguratorEnabled,
  isZeroCostEnabled,
  zeroCostFramesConfig,
  isRopoEnabled,
  isSyteEnabled,
} = useFeatureToggles()

onMounted(() => {
  // if user navigates from PLP to out of stock variant, redirect to first in stock variant
  const cheapestVariant = variantsSortedByPrice.value?.[0]

  if (
    (!queryVariantId.value ||
      parseInt(queryVariantId.value, 10) === cheapestVariant?.id) &&
    (!isVariantAvailable(product.value, cheapestVariant) ||
      !isVariantMaybeFitting(cheapestVariant, faceWidth))
  ) {
    const cheapestVariantAvailable =
      variantsSortedByPrice.value.find(
        (v) =>
          isVariantAvailable(product.value, v) &&
          isVariantMaybeFitting(v, faceWidth),
      ) ||
      variantsSortedByPrice.value.find((v) =>
        isVariantAvailable(product.value, v),
      )

    if (cheapestVariantAvailable) {
      router.replace(
        $fimLocalePath(
          getProductDetailRoute(product.value, {
            ...route.query,
            variantid: cheapestVariantAvailable.id,
          }),
        ),
      )
      setVariantId(cheapestVariantAvailable.id)
    }
  }
})

const {
  product: detailedProduct,
  price,
  quantity,
  fetching,
  packageUnit,
  selectedVariant,
  hasSiblings,
  hasSpecial,
  salesStatus,
  inStock,
  stock,
  breadcrumbs,
  trackProductView,
  backUrl,
  manualUrl,
  complianceUrl,
  productName,
  variantsSortedByPrice,
  isSellableWithoutStock,
  isFromRopoPlp,
  setVariantId,
} = await useProductDetails(props.baseProduct, {
  params: {
    includeSellableForFree: true,
    with: PRODUCT_WITH_RX,
  },
  options: { immediate: true },
})
const product = computed(() => detailedProduct.value || props.baseProduct)

const {
  isBuyXGetYPrioritized,
  isGiftAddedToBasket,
  areGiftConditionsMet,
  highestPriorityPromotion,
} = useProductPromotions(product)

// Product recommendations
const { data: productRecommendations } = useAsyncProductRecommendations(
  [...PRODUCT_RECOMMENDATION_ATTRIBUTES],
  product.value,
)

const recommendationRefXsell = computed(
  () => productRecommendations.value?.recommendationRefXsell,
)

const needsPriceOverlay = computed(() =>
  isHomeTryOnEligible(product.value, $currentShop)
    ? false
    : !isZeroCostProduct.value,
)

const title = computed(() =>
  getProductMetaTitle(toValue(product), toValue(selectedVariant), context),
)
useSeoMeta(getProductSeoMeta(product, selectedVariant, price, context))

onMounted(() => {
  trackProductView(product.value)

  trackPageView({
    title: title.value,
    pageType: 'product',
  })
})

const { getCombinedLowestPriorPrice, getCombinedFrameAndCheapestLensPrice } =
  useProductPrice()

const rxConfiguratorEnabled = computed(
  () => product.value && isRxConfiguratorEnabled(product.value),
)

const { productColor, packageUnits } = useGlasses(
  product,
  variantsSortedByPrice,
)

const { trackAddToBasket, trackCTAClick, aggregateRxConfigurationPrice } =
  await useTrackingEvents()

const { data: basketData, addOrUpdateItems, addItem } = useFimBasket()
const image = useImage()
const syteProductImageUrl = computed(() => {
  if (product.value) {
    return image(product.value.images[0].hash, { trim: 1 }, { provider: 'fim' })
  }
  return undefined
})
const selectedVariantId = computed(() =>
  selectedVariant.value ? [selectedVariant.value.id] : [],
)

const selectedPackageUnit = computed(() => parseInt(packageUnit.value, 10) || 0)
const router = useRouter()

const {
  openRxConfigurator,
  closeRxConfigurator,
  addRxItemsToBasket,
  isRxConfiguratorAvailable,
  rxConfiguratorProps,
} = useRxConfigurator(product, selectedVariant)

const interatedWithVTO = ref(false)
const source = computed(() =>
  interatedWithVTO.value ? 'virtual_try_on' : undefined,
)

const onVtoAction = ({
  product,
  variant,
}: {
  product: Product
  variant: Variant
}) => {
  if (variant.id === selectedVariant.value?.id) {
    openRxConfigurator()
  } else {
    const link = localePath(
      getProductDetailRoute(product, {
        variantId: variant.id,
        rxStep: 1,
      }),
    )

    router.push(link)
  }
}

const localePath = useFimLocalePath()

const { appointmentUrl } = await useRxConfiguratorState()

const rxConfiguratorAddToBasket = async ({
  itemsSerialized,
  rxConfiguration,
}: {
  itemsSerialized: ConfiguratorSerialized
  rxConfiguration: ConfiguratorState
}) => {
  const promotionId =
    highestPriorityPromotion.value?.id && !isBuyXGetYPrioritized.value
      ? highestPriorityPromotion.value.id
      : undefined

  try {
    await addRxItemsToBasket(
      itemsSerialized,
      rxConfiguration,
      promotionId,
      quantity,
      undefined,
      source.value,
    )
    router.push(localePath(routeList.basket))
  } catch (error) {
    console.error('[RXCONFIGURATOR ADD]: ', getErrorMessage(error))
  }
}

const isConsultantUsed = computed(
  () => getQueryParam(route.query, 'consultant', '0') === '1',
)

const addFrameToBasket = async () => {
  const customData = {
    frameOrder: true,
    ...(isConsultantUsed.value && { consultantUsed: true }),
    productType: getProductType(product.value),
  } as FimBasketItemCustomData
  try {
    if (!selectedVariant.value) {
      throw new Error($i18n.t('rx.home_try_on.error.no_variant_selected'))
    }

    const item = basketData.value?.items.find(
      (item) =>
        item?.product?.id === product.value?.id &&
        isFrameOrder(item as FimBasketItem) &&
        item?.variant?.id === selectedVariant.value?.id,
    )

    const quantity = item?.quantity ? item.quantity + 1 : 1

    const addItemPayload = {
      variantId: selectedVariant.value?.id,
      quantity,
      customData,
    }

    await addItem({
      ...addItemPayload,
      existingItemHandling: ExistingItemHandling.ReplaceExisting,
    })

    trackAddToBasket(product.value, Number(quantity), selectedVariant.value, {
      overridePrice: selectedVariant.value?.price,
      source: source.value,
    })

    router.push(localePath(routeList.basket))
  } catch (error) {
    console.error('[RXCONFIGURATOR ADD]: ', error)
  }
}

const benefitsSlug = computed(() =>
  isRopoEnabled.value ? 'ropo-correctional-glasses' : 'correctional-glasses',
)

const isZeroCostProduct = computed(() => {
  return isZeroCostEnabled() && price.value.withoutTax === 0
})

const zeroCostInfoSlug = computed(
  () => zeroCostFramesConfig.value?.storyblokSlug ?? '',
)

const detailNameMapping = {
  modelName: $i18n.t('pdp.details.modelName'),
}

const { addProductToRopoBasket, isRopoSupported: isRopoSupportedFn } =
  useRopoBasketState()

const isRopoSupported = computed(() => isRopoSupportedFn(product.value))

const addItemToRopoBasket = (e: Event) => {
  const result = addProductToRopoBasket(product.value, selectedVariant.value)
  onCTAClick(e)
  if (result?.error !== 'basket-full') {
    router.push(localePath(routeList.ropoBasket))
  }
}

// Price details overlay
const isPriceOverlayVisible = ref(false)
const isOnlinePriceOverlayVisible = ref(false)

const showPriceOverlay = () => {
  if (rxConfiguratorEnabled.value) {
    isOnlinePriceOverlayVisible.value = true
  } else {
    isPriceOverlayVisible.value = true
  }
}

const lowestPrice = computed(() => {
  if (product.value) {
    const combinedLowestPriorPrice = getCombinedLowestPriorPrice(
      unref(product),
      unref(selectedVariant),
    )
    return getLowestPriorPrice(
      price.value,
      combinedLowestPriorPrice,
      Number(quantity.value),
    )
  }
  return undefined
})

const onCTAClick = (event: any) => {
  trackCTAClick({
    productDetails: product.value,
    variant: selectedVariant.value,
    element: event.target,
  })
}

const breadcrumbSchema = computed(() =>
  getBreadcrumbSchemaItems(
    breadcrumbs.value,
    $currentShop.domain ?? '',
    route.fullPath,
    title.value || '',
  ),
)

const combinedPrice = computed(() => {
  const value = getCombinedFrameAndCheapestLensPrice(
    product.value,
    selectedVariant.value,
  )
  return toCurrency(value?.withTax ?? 0)
})

// todo: cleanup useProductDetails
const onSwitchVariant = (index: number) => {
  const entry = packageUnits.value?.[index]
  if (!product.value || entry?.disabled) {
    return
  }

  const variantid =
    entry?.variantId === variantsSortedByPrice.value?.[0]?.id
      ? undefined
      : entry.variantId

  if (entry?.variantId) {
    setVariantId(entry.variantId)
  }
  router.replace(
    $fimLocalePath(
      getProductDetailRoute(product.value, {
        ...route.query,
        variantid,
      }),
    ),
  )

  packageUnit.value = `${index}`
}

setPageState('pageType', 'product')
setPageState('productCategory', ProductCategories.glasses)

// todo: this was a hack to get around UI not updating if I remember correctly. Check if still needed
const deliveryEstimatesKey = ref(0)
scope.run(() => {
  if (import.meta.server) {
    return
  }
  watch([inStock, isSellableWithoutStock], () => {
    deliveryEstimatesKey.value += 1
  })

  watch(queryVariantId, (next) => setVariantId(next), { immediate: true })
})

const productSchema = computed(() =>
  generateProductSchema(
    product.value,
    selectedVariant.value,
    price.value,
    context,
  ),
)

useJsonld(() => productSchema.value)
useJsonld(() => generateBreadcrumbSchema(breadcrumbSchema.value))

const mainProduct = computed(() => {
  const [result] = [product.value, ...(product.value.siblings || [])].sort(
    (a, b) => a.id - b.id,
  )

  return result
})

useHead(() => {
  if (!mainProduct.value) {
    return {}
  }
  return {
    link: [
      {
        rel: 'canonical',
        key: 'canonical',
        href: sanitizeCanonical(
          getUrl(
            $currentShop.domain,
            $fimLocalePath(getProductDetailRoute(mainProduct.value)),
          ),
        ),
      },
    ],
  }
})
</script>

<style lang="scss">
.overlay em {
  font-size: 2rem;
  line-height: 1.5rem;
  vertical-align: top;
  font-style: normal;
}
</style>
